/* Все финансовые агрегаты. ТЗ 7.1, 7.3, 7.5, 7.6, 7.7, 7.9.
   Функции чистые: на вход — массивы записей из БД, на выходе — числа.
   Одна и та же реализация используется API, ботом и мини-аппом. */

import {
  CAPEX_CATS, CASH_GOAL, EXPENSE_CATS, INVEST_CAT, isClosedStage, isSubscribedStage,
  OPEX_CATS, REVENUE_CAT, TRANSFER,
} from "./constants.js";
import { MONTHS_SHORT_RU } from "./constants.js";
import { marginPct, monthKey } from "./format.js";

export const opSign = (o) => (o.type === "Поступление" ? 1 : -1);
export const isIncome = (o) => o.type === "Поступление";
export const isExpense = (o) => o.type === "Расход";
/** Перевод между счетами: денег в группе не прибавилось и не убавилось,
    поэтому во всех сводках он проходит мимо — считаются только доходы и расходы */
export const isTransfer = (o) => o.type === TRANSFER;

/** Своя ли это операция бизнесу: сравниваем по id, «all» пропускает всё.
    Бизнес операции известен через её счёт. */
const inBiz = (o, biz) => biz === "all" || !biz || o.businessId === biz;

/** ТЗ 7.1 — движение денег по журналу: сколько пришло, ушло и что в остатке */
export function bizBalance(operations, biz = "all") {
  const ops = operations.filter((o) => inBiz(o, biz));
  const income = ops.filter(isIncome).reduce((s, o) => s + o.amount, 0);
  const expense = ops.filter(isExpense).reduce((s, o) => s + o.amount, 0);
  return { income, expense, total: income - expense };
}

/** ТЗ 5.1 — сколько денег лежит на счетах. Баланс счёта ведётся руками */
export const accTotal = (a) => a.balance || 0;
export const accountsTotal = (accounts) => accounts.reduce((s, a) => s + accTotal(a), 0);

/** Деньги бизнеса — сумма балансов его счетов */
export const businessTotal = (business) => accountsTotal(business?.accounts || []);

/** Своя ли операция счёту. Перевод касается обоих своих счетов — и того,
    откуда ушли деньги, и того, куда пришли */
const inAcc = (o, acc) => acc === "all" || !acc || o.accountId === acc || o.toAccountId === acc;

/** Отфильтровать операции по бизнесу, счёту и диапазону дат (ISO, включительно).
    Выбранный счёт сам задаёт бизнес, поэтому бизнес тогда не сверяем: перевод
    из другого бизнеса на этот счёт иначе выпал бы из его журнала */
export function filterOps(operations, { biz = "all", acc = "all", from = null, to = null } = {}) {
  const byAcc = acc && acc !== "all";
  return operations.filter((o) => {
    if (byAcc ? !inAcc(o, acc) : !inBiz(o, biz)) return false;
    const d = o.date;
    if (from && d < from) return false;
    if (to && d > to) return false;
    return true;
  });
}

/** Проекты, чьи поступления регулярные, а не разовые.
    Признак берём широко: активное расписание платежей, стадия «на подписке»
    или MRR в сделке — учёт ведут по-разному, а метрика нужна одна. */
export const recurringProjectIds = (projects = []) =>
  new Set(
    projects
      .filter((p) => p.schedule?.active || isSubscribedStage(p.accStage) || (p.dealMrr || 0) > 0)
      .map((p) => p.id)
  );

/** Дашборд финучёта: выручка / опер. расходы / прибыль / маржа + разбивка по категориям.
    Выручка дополнительно делится надвое: регулярная (подписки — MRR, если период
    равен месяцу) и разовые платежи. Что считать подпиской, задаёт `recurringIds`. */
export function financeSummary(operations, recurringIds = new Set()) {
  const revenueOps = operations.filter((o) => isIncome(o) && o.cat === REVENUE_CAT);
  const revenue = revenueOps.reduce((s, o) => s + o.amount, 0);
  const mrr = revenueOps
    .filter((o) => o.proj && recurringIds.has(o.proj))
    .reduce((s, o) => s + o.amount, 0);
  const opex = operations
    .filter((o) => isExpense(o) && o.act === "Операционная")
    .reduce((s, o) => s + o.amount, 0);
  const byCat = {};
  operations.filter(isExpense).forEach((o) => {
    byCat[o.cat] = (byCat[o.cat] || 0) + o.amount;
  });
  const categories = Object.entries(byCat)
    .map(([cat, amount]) => ({ cat, amount }))
    .sort((a, b) => b.amount - a.amount);
  const profit = revenue - opex;
  return {
    revenue, opex, profit, margin: marginPct(profit, revenue), categories,
    mrr, oneTime: revenue - mrr,
  };
}

/** Список месяцев 'YYYY-MM', встречающихся в операциях (по возрастанию) */
export const opMonths = (operations) =>
  [...new Set(operations.map((o) => monthKey(o.date)).filter(Boolean))].sort();

/** ТЗ 7.5 — ОДДС помесячно */
export function cashflowStatement(operations) {
  const months = opMonths(operations);
  const sum = (m, fn) => operations.filter((o) => monthKey(o.date) === m && fn(o)).reduce((s, o) => s + o.amount, 0);

  const rows = [];
  rows.push({
    key: "op-in", section: "Операционная", label: "Поступления от клиентов", sign: 1,
    values: months.map((m) => sum(m, (o) => isIncome(o) && o.act === "Операционная")),
  });
  EXPENSE_CATS.forEach((cat) => {
    const values = months.map((m) => -sum(m, (o) => isExpense(o) && o.act === "Операционная" && o.cat === cat));
    if (values.some((v) => v !== 0)) {
      rows.push({ key: `op-${cat}`, section: "Операционная", label: cat, sign: -1, values });
    }
  });

  const invest = months.map((m) => -sum(m, (o) => isExpense(o) && o.act === "Инвестиционная"));
  const investIn = months.map((m) => sum(m, (o) => isIncome(o) && o.act === "Инвестиционная"));
  const fin = months.map((m) => sum(m, (o) => isIncome(o) && o.act === "Финансовая"));
  const finOut = months.map((m) => -sum(m, (o) => isExpense(o) && o.act === "Финансовая"));

  const operating = months.map((_, i) => rows.reduce((s, r) => s + r.values[i], 0));
  const investing = months.map((_, i) => invest[i] + investIn[i]);
  const financing = months.map((_, i) => fin[i] + finOut[i]);
  const net = months.map((_, i) => operating[i] + investing[i] + financing[i]);

  return {
    months,
    rows,
    operating,
    investing,
    financing,
    net,
    investRows: [
      { key: "inv-out", label: "Оборудование и капзатраты", values: invest },
      { key: "inv-in", label: "Поступления инвестиционные", values: investIn },
    ].filter((r) => r.values.some((v) => v !== 0)),
    finRows: [
      { key: "fin-in", label: "Инвестиции в бизнес", values: fin },
      { key: "fin-out", label: "Выплаты инвесторам", values: finOut },
    ].filter((r) => r.values.some((v) => v !== 0)),
  };
}

/** ТЗ 7.6 — P&L помесячно (капзатраты не входят в операционные расходы) */
export function profitAndLoss(operations) {
  const months = opMonths(operations);
  const sum = (m, fn) => operations.filter((o) => monthKey(o.date) === m && fn(o)).reduce((s, o) => s + o.amount, 0);

  const revenue = months.map((m) => sum(m, (o) => isIncome(o) && o.cat === REVENUE_CAT));
  const expenseRows = OPEX_CATS.map((cat) => ({
    key: cat,
    label: cat,
    values: months.map((m) => sum(m, (o) => isExpense(o) && o.cat === cat)),
  })).filter((r) => r.values.some((v) => v !== 0));

  const opex = months.map((_, i) => expenseRows.reduce((s, r) => s + r.values[i], 0));
  const profit = months.map((_, i) => revenue[i] - opex[i]);
  const margin = months.map((_, i) => marginPct(profit[i], revenue[i]));

  return { months, revenue, expenseRows, opex, profit, margin, capexCats: CAPEX_CATS };
}

/** ТЗ 7.9 — прибыль проекта */
export const projectProfit = (p) => (p.income || 0) - (p.expenses || 0);

/** ТЗ 7.7 — факт выручки проекта: журнал (по привязке proj) плюс факт, внесённый
    руками (factIncome) — у старых проектов операций нет, деньги записаны цифрой */
export const projectFactRevenue = (operations, project) =>
  (project.factIncome || 0) +
  operations.filter((o) => o.proj === project.id && isIncome(o)).reduce((s, o) => s + o.amount, 0);

export const projectFactExpenses = (operations, project) =>
  (project.factExpenses || 0) +
  operations.filter((o) => o.proj === project.id && isExpense(o)).reduce((s, o) => s + o.amount, 0);

/** ТЗ 7.7 — метрики карточки Dialogic-аккаунтинга */
export function dialogicAccountingMetrics(project, deal, operations) {
  const subscribed = isSubscribedStage(project.accStage);
  return {
    mrr: subscribed ? deal?.mrr || 0 : 0,
    implementationPrice: project.income || 0,
    factRevenue: projectFactRevenue(operations, project),
    costTotal: project.expenses || 0,
  };
}

/** Портфель аккаунтинга: агрегаты по всем проектам бизнеса */
export function accountingPortfolio(projects, deals, operations) {
  const byId = new Map(deals.map((d) => [d.id, d]));
  const open = projects.filter((p) => p.accStage && !isClosedStage(p.accStage));
  return {
    projects: open.length,
    closed: projects.filter((p) => isClosedStage(p.accStage)).length,
    checkInWork: open.reduce((s, p) => s + (p.income || 0), 0),
    mrr: open
      .filter((p) => isSubscribedStage(p.accStage))
      .reduce((s, p) => s + (byId.get(p.dealId)?.mrr || 0), 0),
    factRevenue: projects.reduce((s, p) => s + projectFactRevenue(operations, p), 0),
    cost: projects.reduce((s, p) => s + (p.expenses || 0), 0),
  };
}

/** Во сколько регулярный расход обходится в месяц: сумма за раз × число платежей.
    Числа не заданы — считаем, что платим раз в месяц. */
export const recurringMonthly = (r) => (r.amount || 0) * Math.max(1, (r.days || []).length);

/** Приток по месяцам из журнала: сколько всего денег пришло, по всем направлениям.
    Берём последние `months` месяцев подряд, включая текущий, даже пустые —
    иначе месяц без поступлений просто выпадет и средняя окажется завышенной. */
export function monthlyInflowFromOps(operations, today, months = 3) {
  const [y, m] = String(today || "").split("-").map(Number);
  if (!y || !m) return [];
  const keys = [];
  for (let i = months - 1; i >= 0; i -= 1) {
    const d = new Date(Date.UTC(y, m - 1 - i, 1));
    keys.push(`${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`);
  }
  return keys.map((key) => ({
    month: key,
    m: MONTHS_SHORT_RU[Number(key.slice(5, 7)) - 1],
    v: operations
      .filter((o) => isIncome(o) && monthKey(o.date) === key)
      .reduce((sum, o) => sum + (o.amount || 0), 0),
  }));
}

/** ТЗ 7.3 — агрегаты обзора.
    `cashGoal` приходит из целей (цель с единицей «cashflow»); константа — запасной вариант,
    когда такая цель ещё не заведена. */
export function dashboardTotals({ payments, monthlyInflow, accounts, recurring, cashGoal = CASH_GOAL, subscriptions = [] }) {
  /* Прогноз — это то, чего ещё ждём. Полученный платёж уходит из прогноза:
     он уже записан операцией и считается в кэшфлоу как реальные деньги. */
  const pending = payments.filter((p) => p.status !== "received");
  /* Подписки ждём наравне с плановыми платежами: раньше MRR был виден только
     в расшифровке, а в сумму прогноза не попадал — цифра расходилась со списком */
  const mrr = subscriptionMrr(subscriptions);
  const totalRev = pending.reduce((s, p) => s + (p.amount || 0), 0)
    + mrr.reduce((s, m) => s + m.amount, 0);
  const totalProfit = pending.reduce((s, p) => s + (p.profit || 0), 0)
    + mrr.reduce((s, m) => s + m.profit, 0);
  const avg = monthlyInflow.length
    ? Math.round(monthlyInflow.reduce((s, m) => s + m.v, 0) / monthlyInflow.length)
    : 0;
  const balance = accountsTotal(accounts);
  const burn = recurring.reduce((s, r) => s + recurringMonthly(r), 0);
  return {
    totalRev,
    totalCount: pending.length + mrr.length,
    mrrTotal: mrr.reduce((s, m) => s + m.amount, 0),
    totalProfit,
    totalMargin: marginPct(totalProfit, totalRev),
    avgInflow: avg,
    cashGoal,
    goalPct: cashGoal ? Math.round((avg / cashGoal) * 100) : 0,
    balance,
    burn,
    runway: burn ? +(balance / burn).toFixed(1) : null,
  };
}

/** Прогноз по бизнесу: сколько ещё ждём (без уже полученного) */
export function forecastByBiz(payments) {
  const map = new Map();
  payments.forEach((p) => {
    const cur = map.get(p.biz) || { biz: p.biz, amount: 0, profit: 0, pending: 0, count: 0 };
    cur.amount += p.amount || 0;
    cur.profit += p.profit || 0;
    if (p.status !== "received") cur.pending += p.amount || 0;
    cur.count += 1;
    map.set(p.biz, cur);
  });
  return [...map.values()].sort((a, b) => b.amount - a.amount);
}

/** Следующий ежемесячный платёж по подпискам (ТЗ 6.1.1, 7.7).

    Считаем по аккаунтингу, а не по воронке продаж: MRR в сделке — это ожидание
    менеджера, платит клиент только с той стадии, где проект «на подписке».
    Сумма подписки живёт в сделке, поэтому берём её через проект (`dealMrr`). */
export function subscriptionMrr(projects = []) {
  return projects
    .filter((p) => isSubscribedStage(p.accStage) && (p.dealMrr || 0) > 0)
    .map((p) => ({
      id: p.id,
      client: p.dealCompany || p.name,
      amount: p.dealMrr,
      profit: Math.round(p.dealMrr * 0.8),
      kind: "MRR · следующий платёж",
    }));
}

/** Разбивка «твёрдого прогноза» (ТЗ 6.1.1): неполученные платежи плюс
    следующий ежемесячный платёж по клиентам на подписке. */
export function forecastBreakdown(payments, projects) {
  // Ждём всё, что ещё не получено, независимо от направления —
  // раньше отбирались только платежи МЕРЫ, и остальные в расшифровку не попадали
  const pending = payments
    .filter((p) => p.status !== "received")
    .map((p) => ({ id: p.id, client: p.client, amount: p.amount, profit: p.profit, kind: p.kind, when: p.when, dueDate: p.dueDate }));
  return { pending, mrr: subscriptionMrr(projects) };
}

export { CASH_GOAL, INVEST_CAT, REVENUE_CAT };
